import { BrazeCustomEvent, BrazePurchaseEvent } from './braze';
import { ProductInterface } from './PDPProduct';
import { ExpiryTime } from '@westwing/ui-kit/utils/useExpiryTime';

export type BaseImage = {
    src: string;
    seoSrc: string;
    alt: string;
};

export interface LookListing {
    name: string;
    caption: string;
    color: string;
    isLandscape: boolean;
    trackingName: string;
    slug: string;
    landscapeImage: BaseImage;
    portraitImage: BaseImage;
    roomName: string;
    roomTrackingName: string;
    roomSlug: string;
}

export type PartialLookDetail = Partial<
    Pick<LookListing, 'caption' | 'color' | 'isLandscape' | 'slug' | 'trackingName'> & {
        name: string;
        title: string;
        urlKey: string;
    }
>;

export const CartFlyoutTimeoutKey = 'ww_cart_flyout_timeout';

export enum OverlayIds {
    LOGIN_AND_REGISTER = 'LOGIN_AND_REGISTER',
    ALICE_SHOP2CLUB = 'SHOP2CLUB',
    RESET_PASSWORD = 'RESET_PASSWORD',
    BLOCKED_USER = 'BLOCKED_USER',
    CLUB_SIGNUP_OVERLAY = 'CLUB_SIGNUP_OVERLAY',
}

export enum EventNames {
    UPDATE_LOGIN_STATE = 'UPDATE_LOGIN_STATE',
    UPDATE_WISHLIST = 'UPDATE_WISHLIST',
    REFRESH_ONE_CART = 'REFRESH_ONE_CART',
    UPDATE_CART = 'UPDATE_CART',
    UPDATE_OVERLAYS = 'UPDATE_OVERLAYS',
    SIMULATE_BRAZE_PRODUCT_RATINGS = 'SIMULATE_BRAZE_PRODUCT_RATINGS',
    NOTIFICATION = 'NOTIFICATION',
    LOOKS_WISHLIST = 'LOOKS_WISHLIST',
    LOOK_DETAIL_PAGE = 'LOOK_DETAIL_PAGE',
    PRODUCT_DETAIL_PAGE = 'PRODUCT_DETAIL_PAGE',
    SEND_BRAZE_CUSTOM_EVENT = 'SEND_BRAZE_CUSTOM_EVENT',
    SEND_BRAZE_PURCHASE_EVENT = 'SEND_BRAZE_PURCHASE_EVENT',
    SHOW_OOPS_PAGE = 'SHOW_OOPS_PAGE',
    SHOW_BLOCKED_USER_OVERLAY = 'SHOW_BLOCKED_USER_OVERLAY',
}

export enum GenericActions {
    REFRESH = 'REFRESH',
    SEND = 'SEND',
}

export enum UpdateLoginSateActions {
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT',
    EXISTING_USER_LOGIN_FAIL = 'EXISTING_USER_LOGIN_FAIL',
}

export enum DisplayActions {
    SHOW = 'SHOW',
    HIDE = 'HIDE',
}

export enum StoreActions {
    FETCH = 'FETCH',
    ADD = 'ADD',
    REMOVE = 'REMOVE',
    PRE_FILL = 'PRE_FILL',
}

/**
 * The keys of DisplayType MUST match the DisplayOptionsType of @westwing/ui-kit/NotificationBanner
 */
export enum DisplayType {
    default = 'DEFAULT',
    warning = 'WARNING',
    error = 'ERROR',
}

export interface UpdateOverlayDetail {
    action: DisplayActions;
    id: OverlayIds;
    options?: UpdateOverlayOptions;
}

export interface UpdateCartDetail {
    action: GenericActions;
    shouldShowCartFlyout: boolean;
}

export interface UpdateOverlayOptions {
    isLogin?: boolean;
    redirectUrl?: string;
    email?: string;
    trackingLabel?: string;
    isDismissible?: boolean;
    isUndismissibleFromOutsideClick?: boolean;
}

export interface Event {
    name: EventNames;
    event: (e: EventsType[EventNames]) => void;
}

export enum ShopToClubShownLabel {
    Default = '',
    Banner = 'Promo header banner click',
    PageLoad = '2nd page load',
}

export interface OverlayOptionsType {
    isLogin: boolean;
    data?: { trackingLabel: ShopToClubShownLabel };
}

export interface ICredentials {
    email: string;
    password?: string;
}

export type EventNamesType = keyof EventsType;
export type OverlayIdsType = keyof typeof OverlayIds;
export type DisplayOptionsType = keyof typeof DisplayType;
export type DisplayActionsType = keyof typeof DisplayActions;
export type GenericActionsType = keyof typeof GenericActions;
export type UpdateLoginSateActionsType = keyof typeof UpdateLoginSateActions;
export type StoreActionsType = keyof typeof StoreActions;

export type EventsType = {
    UPDATE_WISHLIST: {
        detail: {
            action: GenericActionsType;
        };
    };
    NOTIFICATION: {
        detail: {
            action: DisplayActionsType;
            message: string;
            autoHideInterval?: number | null;
            isSticky?: boolean;
            displayType: DisplayOptionsType;
            expiryTime?: ExpiryTime;
            dismissable?: boolean;
        };
    };
    UPDATE_OVERLAYS: {
        detail: {
            action: DisplayActionsType;
            id: string;
            options?: OverlayOptionsType;
        };
    };
    UPDATE_LOGIN_STATE: {
        detail: {
            action: UpdateLoginSateActionsType;
        };
    };
    UPDATE_CART: {
        detail: {
            action: GenericActionsType;
            shouldShowCartFlyout: boolean;
        };
    };
    SIMULATE_BRAZE_PRODUCT_RATINGS: {};
    LOOKS_WISHLIST: {
        detail: {
            action: StoreActionsType;
            slug: string;
        };
    };
    LOOK_DETAIL_PAGE: {
        detail: {
            action: StoreActionsType;
            look: PartialLookDetail;
        };
    };
    PRODUCT_DETAIL_PAGE: {
        detail: {
            action: StoreActionsType;
            product: ProductInterface;
        };
    };
    SEND_BRAZE_CUSTOM_EVENT: {
        detail: {
            action: GenericActionsType;
            event: BrazeCustomEvent;
        };
    };
    SEND_BRAZE_PURCHASE_EVENT: {
        detail: {
            action: GenericActionsType;
            event: BrazePurchaseEvent;
        };
    };
    SHOW_OOPS_PAGE: {
        detail: {};
    };
    SHOW_BLOCKED_USER_OVERLAY: {
        detail: {
            credentials: ICredentials;
        };
    };
    REFRESH_ONE_CART: {
        detail: {
            shouldShowCartFlyout: boolean;
        };
    };
};
